import {
  createContext, useCallback, useContext, useMemo, useState } from
'react';

/** All vendor onboarding data, persisted across steps and refreshes. */

export const EMPTY_ONBOARDING = {
  businessName: '', businessType: '', vendorCategory: '', shortDescription: '',
  longDescription: '', yearsInBusiness: '', website: '', contactEmail: '', contactPhone: '',
  serviceTitle: '', serviceCategory: '', serviceDescription: '', includedItems: '',
  addOns: '', eventTypes: [], capacity: '', serviceArea: '', cancellationPolicy: '',
  logoName: '', coverName: '', galleryNames: [],
  logoPhoto: null, coverPhoto: null, galleryPhotos: [],
  basePrice: '', pricingType: 'flat', depositAmount: '25', minimumBooking: '',
  addOnPricing: '', discount: '',
  address: '', city: '', state: '', zip: '', serviceRadius: '25', travelFee: '',
  // Catering only (main-branch CateringOnboard parity): kitchen & pickup
  operatingFrom: 'commercial_kitchen', pickupAvailable: true,
  pickupAddress: '', pickupInstructions: '', prepTimeMinutes: '60', advanceNoticeHours: '24',
  permitNumber: '', complianceAttested: false,
  daysAvailable: [], timeStart: '09:00', timeEnd: '22:00', blackoutDates: '',
  bookingNotice: '48 hours', maxBookingsPerDay: '2',
  termsAccepted: false
};

const STORAGE_KEY = 'vgo.onboarding';
const OnboardingContext = createContext(null);

export function OnboardingProvider({ children }) {
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const restored = raw ? { ...EMPTY_ONBOARDING, ...JSON.parse(raw) } : EMPTY_ONBOARDING;
      // Blob URLs are valid only in the browser session that created them.
      // Clear sandbox previews after a refresh instead of showing broken media.
      if (restored.coverPhoto?.url?.startsWith('blob:')) {
        restored.coverPhoto = null;
        restored.coverName = '';
      }
      if (restored.logoPhoto?.url?.startsWith('blob:')) {
        restored.logoPhoto = null;
        restored.logoName = '';
      }
      restored.galleryPhotos = (restored.galleryPhotos || []).filter((photo) => !photo.url?.startsWith('blob:'));
      restored.galleryNames = restored.galleryPhotos.map((photo) => photo.name);
      return restored;
    } catch {
      return EMPTY_ONBOARDING;
    }
  });

  const update = useCallback((patch) => {
    setData((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Keep in-memory progress working if storage is unavailable/full.
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setData(EMPTY_ONBOARDING);
  }, []);

  const value = useMemo(() => ({ data, update, reset }), [data, update, reset]);
  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding must be used inside OnboardingProvider');
  return ctx;
}
